import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type ReactNode, type RefObject } from 'react';
import { Button } from '@/components/ui/Button';
import { CoachModal } from '@/features/coach/components/CoachModal';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { publicAppSettingsKey } from '@/lib/queryClient';
import { getUploadedClubLogoUrl } from '@/lib/matchLogos';
import { FeedbackAuthoringHost, type FeedbackAuthoringHostHandle } from './FeedbackAuthoringHost';
import { FeedbackConversationHost } from './FeedbackConversationHost';
import { useCanonicalScope } from './useCanonicalFeedback';
import { useRoster } from '@/features/coach/hooks/useRoster';
import { useMatches } from '@/features/matches/useMatches';
import { CanonicalRecapHost } from '@/features/recaps/CanonicalRecapHost';
import { useRecapScope } from '@/features/recaps/useCanonicalRecap';
import { captureDraftContext, isCurrentDraftContext, useComposerDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import { emptyScene } from '@/lib/canvas/board-schema';
import { captureRecapBoundary, getAppSettings, getCoachResourceRevision, observeAuthTokenChanges } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';
import { feedbackNavigationTarget } from '@/lib/feedbackNavigation';
import { listResourceEditorContinuations, loadResourceEditorContinuation,resourceEditorContinuationKind } from '@/lib/resourceEditorContinuation';
import { createResourceLibraryAction, type ResourceLibraryAction } from '@/features/resource-editor/resourceLibraryActions';
import { initialResourceLibraryState, type ResourceLibraryState } from '@/features/resource-library/useResourceLibrary';
import { createResourceBoardSession, type ResourceBoardSession } from '@/features/resource-editor/resourceEditorSession';
import type { FeedbackResourceCreationRequest, FeedbackResourceLinkTarget } from '@/features/feedback-authoring/resourceCreationContract';
import {createResourceVideoSession,emptyResourceVideo,type ResourceVideoSession,type ResourceVideoValue} from '@/features/resource-editor/resourceVideoSession';
import type { Player, ResourcePin, ResourceKind, ResourceDetailResponse } from '@/lib/types';
import type { CoachReviewSearch, CoachShellSearch } from '@/router';

type ReturnLocation={kind:'coach';search:CoachShellSearch}|{kind:'review';search:CoachReviewSearch};
type ResourceEntryBase={id:string;context:DraftContext;error:string|null;returnLocation:ReturnLocation;openDetails?:boolean;
  videoView:{sourceKey:string;seconds:number;timeline:'full'|'detail';rate:number}};
export type ResourceBoardEntry=ResourceEntryBase & (
  {kind:'tactical_board';session:ResourceBoardSession|null}|{kind:'moment'|'clip';session:ResourceVideoSession|null});
function entrySession(base:ResourceEntryBase,kind:ResourceKind,detail:ResourceDetailResponse|null,
  target:Parameters<typeof createResourceBoardSession>[3],seed?:ResourceVideoValue):ResourceBoardEntry {
  return kind==='tactical_board'?{...base,kind,session:createResourceBoardSession(base.context,base.id,detail?{kind:'saved',detail}
    :{kind:'new',value:{scene:emptyScene(),label:''}},target)}
    :{...base,kind,session:createResourceVideoSession(base.context,base.id,kind,detail?{kind:'saved',detail}
      :{kind:'new',value:seed??emptyResourceVideo(kind)},target)};
}
const sourceFor=(kind:ResourceKind)=>kind==='tactical_board'?'tactical_board' as const:'video' as const;
type Workspace={
  authoring:RefObject<FeedbackAuthoringHostHandle>;authoringActive:boolean;players:Player[];
  setOtherOverlay(active:boolean):void;entries:ResourceBoardEntry[];
  openBoard(request?:FeedbackResourceCreationRequest):void;
  openResource(kind:ResourceKind,request?:FeedbackResourceCreationRequest,seed?:ResourceVideoValue):void;
  transitionResource(entry:ResourceBoardEntry,kind:ResourceKind,seed?:ResourceVideoValue,openDetails?:boolean):void;
  ensureBoard(entryId?:string,pin?:ResourcePin,kind?:ResourceKind):void;
  showFeedback(entry:ResourceBoardEntry):void;
  linkFeedback(entry:ResourceBoardEntry):Promise<void>;
  resourceLibrary: ResourceLibraryState; setResourceLibrary(state: ResourceLibraryState): void;
  openSavedResource(pin: ResourcePin, kind: ResourceKind): void;
  resourceAction(action: 'archive' | 'duplicate', pin: ResourcePin, kind: ResourceKind): void;
  resourceRecoveries: { id: string; label: string }[]; recoverResourceAction(entryId: string): void;
  resourceLinkRecoveries: { id: string; label: string }[]; recoverResourceLink(entryId: string): void;
  linkSavedResource(pin: ResourcePin, kind: ResourceKind): void;
};
type MetadataEntry = { id: string; owner: ResourceLibraryAction; pin?: ResourcePin; kind?: ResourceKind; action?: 'archive' | 'duplicate' };
type SavedLink = { id: string; context: DraftContext; boundary: ReturnType<typeof captureRecapBoundary>; pin: ResourcePin; kind: ResourceKind; targetId: string; busy: boolean; error: string | null; done: boolean };
const Context=createContext<(Omit<Workspace,'setOtherOverlay'> & {setOverlayOwner(owner:string,active:boolean):void})|null>(null);
export function useCanonicalCoachWorkspace():Workspace|null{
  const workspace=useContext(Context),owner=useId(),register=workspace?.setOverlayOwner;
  const setOtherOverlay=useCallback((active:boolean)=>register?.(owner,active),[register,owner]);
  useEffect(()=>()=>register?.(owner,false),[register,owner]);
  return useMemo(()=>workspace?{...workspace,setOtherOverlay}:null,[workspace,setOtherOverlay]);
}

/** DEV-only native integration. This parent outlives /coach ↔ /coach/review;
 * route disclosure never owns conversation writing, RHF forms or board sessions. */
export function CanonicalCoachWorkspaceHost({children}:{children:ReactNode}) {
  const navigate=useNavigate(),location=useRouterState({select:state=>state.location});
  const context=useComposerDraftContext(),scope=useCanonicalScope('coach'),roster=useRoster();
  const recapScope=useRecapScope('coach'),matches=useMatches();
  const settings=useQuery({queryKey:publicAppSettingsKey(),queryFn:getAppSettings,staleTime:60_000});
  const tenantTeamName=useScopeStore(state=>state.active?.team.name);
  const authoring=useRef<FeedbackAuthoringHostHandle>(null);
  const [authoringActive,setAuthoringActive]=useState(false),[overlayOwners,setOverlayOwners]=useState<ReadonlySet<string>>(()=>new Set());
  const otherOverlay=overlayOwners.size>0;
  const setOverlayOwner=useCallback((owner:string,active:boolean)=>setOverlayOwners(previous=>{
    if(previous.has(owner)===active)return previous;
    const next=new Set(previous);if(active)next.add(owner);else next.delete(owner);return next;
  }),[]);
  const [recapOverlay,setRecapOverlay]=useState(false);
  const [entries,setEntries]=useState<ResourceBoardEntry[]>([]),owned=useRef(entries);
  const libraries = useRef(new Map<string, ResourceLibraryState>()), [, refreshLibrary] = useState(0);
  const libraryKey = JSON.stringify(context);
  const resourceLibrary = libraries.current.get(libraryKey) ?? initialResourceLibraryState();
  const setResourceLibrary = (state: ResourceLibraryState) => { libraries.current.set(libraryKey, state); refreshLibrary(value => value + 1); };
  const metadata = useRef(new Map<string, MetadataEntry>()), [activeMetadata, setActiveMetadata] = useState<string | null>(null);
  const [, refreshMetadata] = useState(0);
  const savedLinks = useRef(new Map<string, SavedLink>()), [activeLink, setActiveLink] = useState<string | null>(null);
  const [actionError, setActionError] = useState(false);
  useEffect(() => {
    const changed = () => {
      // Observe the event itself, including A→B→A in one React batch. Keep
      // recovery records, but a retired record cannot own the foreground.
      savedLinks.current.forEach(link => link.boundary.current());
      metadata.current.forEach(entry => entry.owner.getState());
      refreshMetadata(value => value + 1);
    };
    const auth = useAuthStore.subscribe(changed), selection = useScopeStore.subscribe(changed), token = observeAuthTokenChanges(changed);
    return () => { auth(); selection(); token(); };
  }, []);
  const live=useRef(context);live.current=context;
  const reads=useRef(new Map<string,AbortController>());
  const blankEntries=useRef<Partial<Record<ResourceKind,string>>>({});
  const players=roster.error||!scope?[]:(roster.data?.players??[]).filter(player=>player.team_id===scope.teamId
    && (player.season_id===null||player.season_id===scope.seasonId));
  const here=():ReturnLocation=>location.pathname==='/coach'
    ?{kind:'coach',search:{...location.search} as CoachShellSearch}:{kind:'review',search:{...location.search} as CoachReviewSearch};
  const back=(entry:ResourceBoardEntry)=>entry.returnLocation.kind==='coach'
    ?navigate({to:'/coach',search:entry.returnLocation.search}):navigate({to:'/coach/review',search:entry.returnLocation.search});
  const insert=(entry:ResourceBoardEntry)=>{
    const previous=owned.current.find(old=>old.id===entry.id)?.session;
    if(previous&&previous!==entry.session)previous.dispose();
    owned.current=[...owned.current.filter(old=>old.id!==entry.id),entry];setEntries(owned.current);
  };
  const mountEpoch=useRef(0);
  useEffect(()=>{const epoch=++mountEpoch.current;return()=>{
    // Strict Mode rehearses effects while keeping this owner. Retire on an
    // actual departure; each session independently fences auth/scope immediately.
    queueMicrotask(()=>{if(mountEpoch.current===epoch){reads.current.forEach(read=>read.abort());owned.current.forEach(entry=>entry.session?.dispose());metadata.current.forEach(entry=>entry.owner.dispose());savedLinks.current.forEach(link=>link.boundary.dispose());}});
  };},[]);
  function openBoard(request?:FeedbackResourceCreationRequest){openResource('tactical_board',request);}
  function createEntry(kind:ResourceKind,captured:DraftContext,target:Parameters<typeof createResourceBoardSession>[3],
    seed?:ResourceVideoValue,returnLocation:ReturnLocation=here(),initialHint?:Pick<CoachReviewSearch,'match_id'|'slot'>,openDetails=false){
    const id=crypto.randomUUID();blankEntries.current[kind]=id;
    const base={id,context:captured,error:null,returnLocation,openDetails,videoView:{sourceKey:'',seconds:0,timeline:kind==='clip'?'detail' as const:'full' as const,rate:1}};
    insert(entrySession(base,kind,null,target,seed));authoring.current?.suspend();
    void navigate({to:'/coach/review',search:{source:sourceFor(kind),resource_entry:id,...initialHint}});
  }
  function openResource(kind:ResourceKind,request?:FeedbackResourceCreationRequest,seed?:ResourceVideoValue,initialHint?:Pick<CoachReviewSearch,'match_id'|'slot'>){
    const captured=live.current;
    if(!captured||!isCurrentDraftContext(captured)||request?.signal.aborted)return;
    if(request&&(request.kind!==kind||request.scope.actorId!==captured.actorId
      ||request.scope.clubId!==captured.clubId||request.scope.teamId!==captured.teamId||request.scope.seasonId!==captured.seasonId))return;
    const target=request?{kind:'feedback' as const,entry_id:request.entryId,creation_attempt_id:request.creationAttemptId}:{kind:'standalone' as const};
    createEntry(kind,captured,target,seed,here(),initialHint);
  }
  function transitionResource(entry:ResourceBoardEntry,kind:ResourceKind,seed?:ResourceVideoValue,openDetails=false){
    if(!isCurrentDraftContext(entry.context)||!owned.current.includes(entry)||!entry.session)return;
    const state=entry.session.getState(),target=entry.session.getReturnTarget();
    if(state.status!=='available'||state.dirty||state.command||state.refreshing||state.linking||!target)return;
    // A child is another independent Resource attempt within the same original
    // Feedback entry. The parent's receipt and attempt stay available separately.
    const next=target.kind==='feedback'?{...target,creation_attempt_id:crypto.randomUUID()}:target;
    createEntry(kind,entry.context,next,seed,entry.returnLocation,undefined,openDetails);
  }
  function ensureBoard(entryId?:string,pin?:ResourcePin,kind:ResourceKind='tactical_board',returnTo?:ReturnLocation){
    const captured=live.current;if(!captured||!isCurrentDraftContext(captured))return;
    if(!entryId&&!pin){
      const kept=owned.current.find(entry=>entry.id===blankEntries.current[kind]&&isCurrentDraftContext(entry.context));
      if(kept){
        if(kept.session?.getState().status==='unavailable')ensureBoard(kept.id);
        void navigate({to:'/coach/review',search:{source:sourceFor(kept.kind),resource_entry:kept.id,
          ...(kept.kind!=='tactical_board'&&(location.search as CoachReviewSearch).match_id
            ?{match_id:(location.search as CoachReviewSearch).match_id,slot:(location.search as CoachReviewSearch).slot}:{})},replace:true});
      }else openResource(kind,undefined,undefined,kind!=='tactical_board'&&location.pathname==='/coach/review'
        ?{match_id:(location.search as CoachReviewSearch).match_id,slot:(location.search as CoachReviewSearch).slot}:undefined);return;
    }
    const id=entryId??crypto.randomUUID();
    if(owned.current.some(entry=>entry.id===id&&entry.session&&entry.session.getState().status!=='unavailable'&&isCurrentDraftContext(entry.context))||reads.current.has(id))return;
    const abort=new AbortController();reads.current.set(id,abort);
    const entry:ResourceBoardEntry={id,kind,context:captured,session:null,error:null,returnLocation:returnTo??owned.current.find(value=>value.id===id)?.returnLocation??{kind:'coach',search:{tab:'board'}},
      videoView:{sourceKey:'',seconds:0,timeline:kind==='clip'?'detail':'full',rate:1}};insert(entry);
    void (async()=>{
      let row:ReturnType<typeof loadResourceEditorContinuation>=null;
      try{
        row=entryId?loadResourceEditorContinuation(captured,id):null;
        if(row?.return_target.kind==='assist')throw Error('Assist return belongs to its original review');
        if(entryId&&!row&&!pin)throw Error('missing');
        // An untouched saved editor has no command journal yet. Its exact URL
        // may reopen it, but can never supersede a retained command or receipt.
        const selected=row?(row.pending?row.origin:row.accepted?.receipt??row.origin):pin;
        const detail=selected?await getCoachResourceRevision(selected,{scope:captured,signal:abort.signal}):null;
        if(abort.signal.aborted||!isCurrentDraftContext(captured))return;
        if(detail&&(!detail.editor||detail.resource.availability!=='available'
          ||detail.resource.resource_id!==selected?.resource_id||detail.resource.revision_id!==selected?.revision_id))throw Error('unavailable');
        if(!detail&&!row?.pending)throw Error('unavailable');
        const selectedKind=detail?.resource.kind??(row?resourceEditorContinuationKind(row):kind);
        insert(entrySession({...entry,context:captureDraftContext(captured)},selectedKind,detail,row?.return_target??{kind:'standalone'}));
        if(!entryId||pin&&(pin.resource_id!==selected?.resource_id||pin.revision_id!==selected?.revision_id)){
          void navigate({to:'/coach/review',search:{source:sourceFor(selectedKind),resource_entry:id,
            ...(selected?{resource_id:selected.resource_id,resource_revision:selected.revision_id}:{})},replace:true});
        }
      }catch{
        if(!abort.signal.aborted&&isCurrentDraftContext(captured)){
          if(row?.return_target.kind!=='assist'&&(row?.pending||row?.accepted)){
            try{
              const restored=entrySession(entry,resourceEditorContinuationKind(row),null,row.return_target);
              restored.session!.withholdContent();insert(restored);
            }catch{insert({...entry,error:'This retained Resource operation could not be read. No new operation was sent.'});}
          }else insert({...entry,error:'This saved Resource editor could not be opened. Your retained operation has not been changed.'});
        }
      }finally{reads.current.delete(id);}
    })();
  }
  function showFeedback(entry:ResourceBoardEntry){
    if(!isCurrentDraftContext(entry.context)||!entry.session)return;
    const target=entry.session.getReturnTarget();
    if(target?.kind==='assist')return;
    if(target?.kind==='feedback'){
      void back(entry);if(!authoring.current?.activateEntry(target.entry_id))throw Error('Original Feedback entry unavailable');
    }else authoring.current?.open();
  }
  async function linkFeedback(entry:ResourceBoardEntry):Promise<void>{
    if(!isCurrentDraftContext(entry.context)||!entry.session)throw Error('Resource context changed');
    const session=entry.session;session.setActive(true);
    let target=session.getReturnTarget();if(!target)throw Error('Resource context changed');
    if(target.kind==='assist')throw Error('Assist return belongs to its original review');
    if(target.kind==='standalone'){
      // Finish this editor read before the chooser changes authoringActive.
      // CoachReview's activity cleanup aborts reads during that transition.
      // Destination selection must bind and activate synchronously; its pin
      // admission still freshly authorizes the exact saved revision.
      await session.refresh();
      // Resource-first chooses a real kept/new destination explicitly. Its
      // exact selected entry is bound before workspace creation or admission.
      await new Promise<void>((resolve, reject) => {
        const host = authoring.current; if (!host) { reject(Error('Feedback entry unavailable')); return; }
        host.chooseResourceDestination((entryId, activate) => {
          try {
            session.setActive(true);
            const selected = { kind: 'feedback' as const, entry_id: entryId, creation_attempt_id: crypto.randomUUID() };
            session.beginFeedbackReturn(selected); void back(entry);
            activate(destination => completeReturn(destination).then(resolve, reject), () => reject(Error('Feedback return cancelled; saved Resource retained')));
          } catch(error) { reject(error); }
        }, () => reject(new DOMException('Feedback return cancelled; saved Resource retained', 'AbortError')));
      });
      return;
    }
    const targetId=target.entry_id;
    async function completeReturn(destination: FeedbackResourceLinkTarget) {
      session.setActive(true);
      try {
        const outcome = await session.returnToFeedback(destination);
        if (outcome.status === 'retained') {
          if (isCurrentDraftContext(entry.context)) { authoring.current?.suspend(); void navigate({ to: '/coach/review', search: { source: sourceFor(entry.kind), resource_entry: entry.id } }); }
          throw Error(outcome.reason);
        }
      } finally { if (session.getState().status !== 'unavailable') session.setActive(false); }
    }
    await new Promise<void>((resolve,reject)=>{
      const ready=(destination:FeedbackResourceLinkTarget)=>{
        void completeReturn(destination).then(resolve, reject);
      };
      const cancelled=()=>reject(new DOMException('Feedback return cancelled; saved Resource retained','AbortError'));
      const host=authoring.current;if(!host){cancelled();return;}
      void back(entry);
      if(!host.activateEntry(targetId,ready,cancelled))reject(Error('Original Feedback entry unavailable'));
    });
  }
  function resourceAction(action: 'archive' | 'duplicate', pin: ResourcePin, kind: ResourceKind) {
    const captured = live.current; if (!captured || !isCurrentDraftContext(captured)) return;
    const pending = [...metadata.current.values()].find(entry => entry.pin?.resource_id === pin.resource_id
      && entry.owner.getState().current && (entry.owner.getState().busy || entry.owner.getState().command));
    if (pending) { setActiveMetadata(pending.id); return; }
    const id = crypto.randomUUID(); let owner: ResourceLibraryAction;
    try { owner = createResourceLibraryAction(captured, id); } catch { setActionError(true); return; }
    metadata.current.set(id, { id, owner, pin: { resource_id: pin.resource_id, revision_id: pin.revision_id }, kind, action });
    owner.subscribe(() => refreshMetadata(value => value + 1)); setActiveMetadata(id);
    void owner.start(action, pin, kind).catch(() => {});
  }
  function recoverResourceAction(id: string) {
    const captured = live.current; if (!captured || !isCurrentDraftContext(captured) || owned.current.some(entry => entry.id === id)) return;
    const existing = metadata.current.get(id);
    if (!existing?.owner.getState().current) {
      existing?.owner.dispose();
      try { const owner = createResourceLibraryAction(captured, id), row = loadResourceEditorContinuation(captured, id);
        const command = row?.pending ?? row?.accepted?.command;
        metadata.current.set(id, { id, owner, pin: row?.origin ?? undefined, kind: row ? resourceEditorContinuationKind(row) : undefined,
          action: command?.action === 'archive' || command?.action === 'duplicate' ? command.action : undefined }); owner.subscribe(() => refreshMetadata(value => value + 1)); }
      catch { return; }
    }
    setActiveMetadata(id);
  }
  const resourceRecoveries: Workspace['resourceRecoveries'] = [];
  if (context) {
    try { for (const row of listResourceEditorContinuations(context)) {
      const command = row.pending ?? row.accepted?.command;
      if (row.return_target.kind === 'standalone' && (command?.action === 'archive' || command?.action === 'duplicate') && !owned.current.some(entry => entry.id === row.entry_id))
        resourceRecoveries.push({ id: row.entry_id, label: `${command.action === 'archive' ? 'Archive' : 'Duplicate'} Resource · ${row.pending ? 'Check original result' : 'Saved result'}` });
    } } catch { /* A blocked journal cannot be guessed or automatically dispatched. */ }
  }
  function linkSavedResource(pin: ResourcePin, kind: ResourceKind) {
    const captured = live.current, host = authoring.current; if (!captured || !isCurrentDraftContext(captured) || !host) return;
    const kept = [...savedLinks.current.values()].find(link => !link.done && link.boundary.current() && link.kind === kind
      && link.pin.resource_id === pin.resource_id && link.pin.revision_id === pin.revision_id);
    if (kept) { setActiveLink(kept.id); return; }
    host.chooseResourceDestination((targetId, activate) => {
      const job: SavedLink = { id: crypto.randomUUID(), context: captured, boundary: captureRecapBoundary({ ...captured, view: 'coach' }), pin: { resource_id: pin.resource_id, revision_id: pin.revision_id }, kind, targetId, busy: true, error: null, done: false };
      savedLinks.current.set(job.id, job);
      activate(target => { void admitSaved(job, target); }, () => {
        job.busy = false; job.error = 'Your Resource is saved. The selected Feedback entry could not be opened.';
        if (job.boundary.current()) { authoring.current?.suspend(); setActiveLink(job.id); }
      });
    }, () => {});
  }
  async function admitSaved(job: SavedLink, target: FeedbackResourceLinkTarget) {
    if (!job.boundary.current()) { job.busy = false; return; }
    if (target.entryId !== job.targetId || target.signal.aborted) {
      job.busy = false; job.error = 'The original Feedback entry is unavailable. Your saved Resource and writing are kept.';
      authoring.current?.suspend(); setActiveLink(job.id); return;
    }
    job.busy = true; job.error = null; refreshMetadata(value => value + 1);
    try {
      const outcome = await target.admitPin({ kind: job.kind, pin: job.pin });
      if (!job.boundary.current()) return;
      if (outcome.status === 'retained') {
        job.error = outcome.reason === 'replacement-required' ? 'A different version is selected. Use its explicit Update version action; this link was not replaced.'
          : outcome.reason === 'resource-limit' ? 'This Feedback already has ten Resources. Its selections are kept.' : 'Resource saved; link not completed. Your original Feedback writing is kept.';
        if (authoring.current?.isEntryActive(job.targetId)) { authoring.current.suspend(); setActiveLink(job.id); }
      } else { job.done = true; setActiveLink(value => value === job.id ? null : value); }
    } catch {
      job.error = 'Resource saved; link not completed. Your original Feedback writing is kept.';
      if (job.boundary.current() && authoring.current?.isEntryActive(job.targetId)) { authoring.current.suspend(); setActiveLink(job.id); }
    }
    finally { job.busy = false; refreshMetadata(value => value + 1); }
  }
  function retrySaved(job: SavedLink) {
    if (job.busy || !job.boundary.current()) return;
    setActiveLink(null); job.busy = true;
    const cancelled = () => {
      job.busy = false; job.error = 'The original Feedback entry is unavailable. Your saved Resource and writing are kept.';
      if (job.boundary.current()) { authoring.current?.suspend(); setActiveLink(job.id); refreshMetadata(value => value + 1); }
    };
    if (!authoring.current?.activateEntry(job.targetId, target => { void admitSaved(job, target); }, cancelled)) cancelled();
  }
  const heldMetadata = activeMetadata ? metadata.current.get(activeMetadata) : null;
  const heldLink = activeLink ? savedLinks.current.get(activeLink) : null;
  const metadataEntry = heldMetadata?.owner.getState().current ? heldMetadata : null;
  const savedLink = heldLink?.boundary.current() ? heldLink : null;
  useEffect(() => {
    if (activeMetadata && !metadataEntry) setActiveMetadata(null);
    if (activeLink && !savedLink) setActiveLink(null);
  }, [activeMetadata, metadataEntry, activeLink, savedLink]);
  const coachPage=location.pathname==='/coach';
  const target=coachPage?feedbackNavigationTarget(location.search):null;
  return <Context.Provider value={{authoring,authoringActive,players,setOverlayOwner,entries,openBoard,openResource,transitionResource,ensureBoard,showFeedback,linkFeedback,
    resourceLibrary, setResourceLibrary, openSavedResource: (pin, kind) => ensureBoard(undefined, pin, kind, here()),
    resourceAction, resourceRecoveries, recoverResourceAction, linkSavedResource,
    resourceLinkRecoveries: [...savedLinks.current.values()].filter(link => !link.done && link.boundary.current()).map(link => ({ id: link.id, label: link.busy ? 'Original link is still pending' : 'Original Feedback writing is kept' })),
    recoverResourceLink: id => { const link = savedLinks.current.get(id); if (link?.boundary.current()) setActiveLink(id); } }}>
    <CanonicalRecapHost scope={recapScope} matches={matches.data?.filter(match=>match.season_id===recapScope?.seasonId)}
      clubLogoUrl={getUploadedClubLogoUrl(settings.data)} tenantTeamName={tenantTeamName}
      active={coachPage&&!authoringActive&&!otherOverlay&&!actionError&&!metadataEntry&&!savedLink}
      requestedRecapId={coachPage?(location.search as CoachShellSearch).recap_id:undefined}
      onCloseDestination={()=>void navigate({to:'/coach',search:{...(location.search as CoachShellSearch),recap_id:undefined}})}
      onOverlayChange={setRecapOverlay}>{children}</CanonicalRecapHost>
    <FeedbackConversationHost view="coach" target={target} players={players}
      invalid={coachPage&&!target&&!!(location.search.feedback_id||location.search.feedback_player_id)}
      suspended={!coachPage||authoringActive||otherOverlay||recapOverlay||actionError||!!metadataEntry||!!savedLink} onClose={()=>void navigate({to:'/coach',search:{...(location.search as CoachShellSearch),feedback_id:undefined,feedback_player_id:undefined}})}
      onEdit={entry=>authoring.current?.edit(entry)}/>
    <FeedbackAuthoringHost ref={authoring} players={players.filter(player=>player.active).map(player=>({id:player.id,label:player.display_name}))}
      onActiveChange={setAuthoringActive} onCreateResource={request=>openResource(request.kind,request)}/>
    {metadataEntry && <ResourceActionDialog entry={metadataEntry} onClose={() => setActiveMetadata(null)}
      onEdit={(pin, kind) => { setActiveMetadata(null); ensureBoard(undefined, pin, kind, here()); }}
      onLink={(pin, kind) => { setActiveMetadata(null); linkSavedResource(pin, kind); }} />}
    {actionError && <CoachModal title="Resource action unavailable" onClose={() => setActionError(false)}><p role="alert">The recovery record could not be prepared. No Resource command was sent. Close and try the action again.</p></CoachModal>}
    {savedLink && savedLink.boundary.current() && <CoachModal title="Resource saved" onClose={() => setActiveLink(null)}>
      <p role="alert">{savedLink.error}</p><Button variant="outline" disabled={savedLink.busy} onClick={() => retrySaved(savedLink)}>Retry original link</Button>
      <p className="mt-3 text-xs text-ink-muted">Only linking is retried. The Resource will not be saved again.</p>
    </CoachModal>}
  </Context.Provider>;
}

function ResourceActionDialog({ entry, onClose, onEdit, onLink }: { entry: MetadataEntry; onClose(): void;
  onEdit(pin: ResourcePin, kind: ResourceKind): void; onLink(pin: ResourcePin, kind: ResourceKind): void }) {
  useSyncExternalStore(entry.owner.subscribe, entry.owner.revision, entry.owner.revision);
  const state = entry.owner.getState();
  if (!state.current) return null;
  const retry = () => { void (state.command ? entry.owner.retry() : entry.pin && entry.action && entry.kind
    ? entry.owner.start(entry.action, entry.pin, entry.kind) : Promise.reject(Error('Original action unavailable'))).catch(() => {}); };
  return <CoachModal title={state.action === 'archive' ? 'Archive Resource' : 'Duplicate Resource'} onClose={onClose}>
    {state.busy ? <p role="status">Checking the Resource result…</p> : state.receipt ? <>
      <p role="status">{state.action === 'archive' ? 'Resource archived. Existing Feedback keeps its selected version.' : 'Resource duplicated and saved independently.'}</p>
      {state.action === 'duplicate' && state.kind && <div className="mt-3 flex gap-2"><Button onClick={() => onEdit(state.receipt!, state.kind!)}>Open editor</Button><Button variant="outline" onClick={() => onLink(state.receipt!, state.kind!)}>Link to Feedback</Button></div>}
      <Button variant="ghost" onClick={() => { entry.owner.dismiss(); onClose(); }}>Done</Button>
    </> : <>
      <p role="alert">{state.command?.status === 'accepted' ? 'The server accepted this operation. Its local recovery record still needs to be confirmed.' : state.error ?? 'An earlier Resource operation needs its original result checked.'}</p>
      <Button variant="outline" onClick={retry}>{state.command ? 'Check original operation' : 'Retry action'}</Button>
      {state.command?.status === 'rejected' && <Button variant="ghost" onClick={() => { entry.owner.releaseRejected(); onClose(); }}>Keep Resource and close</Button>}
    </>}
  </CoachModal>;
}
